import { creativeBiomeWeights, CREATIVE_WORLD, type CreativeBiomeWeights } from './CreativeBiome';
import { legacyThemeEnvironment, type LegacyThemeEnvironment } from './LegacyThemeCatalog';

export type CreativeEnvironmentSettings = Pick<
  LegacyThemeEnvironment,
  'ambientColor' | 'fogColor' | 'fogNear' | 'fogFar' | 'bloom'
>;
const environments = CREATIVE_WORLD.regions.map(({ theme }) => legacyThemeEnvironment(theme));

/** Use the terrain's continuous weights, including every catalog landscape. */
export function blendCreativeEnvironment(
  weights: CreativeBiomeWeights,
): CreativeEnvironmentSettings {
  const blend = (read: (environment: LegacyThemeEnvironment) => number): number =>
    environments.reduce(
      (sum, environment, index) => sum + read(environment) * (weights[index] ?? 0),
      0,
    );
  const color = (key: 'ambientColor' | 'fogColor'): [number, number, number] => [
    blend((environment) => environment[key][0]),
    blend((environment) => environment[key][1]),
    blend((environment) => environment[key][2]),
  ];
  return {
    ambientColor: color('ambientColor'),
    fogColor: color('fogColor'),
    fogNear: blend((environment) => environment.fogNear),
    fogFar: blend((environment) => environment.fogFar),
    bloom: {
      blurSize: blend((environment) => environment.bloom.blurSize),
      intensity: blend((environment) => environment.bloom.intensity),
      exposureCutoff: blend((environment) => environment.bloom.exposureCutoff),
    },
  };
}

export class CreativeEnvironment {
  private weights?: CreativeBiomeWeights;

  update(
    x: number,
    z: number,
    deltaSeconds: number,
    cameraHeight = 1,
  ): CreativeEnvironmentSettings {
    const surface = creativeBiomeWeights(x, z);
    const depth = Math.max(0, Math.min(1, (-cameraHeight - 0.2) / 1));
    const submerged = depth * depth * (3 - 2 * depth);
    const target = surface.map(
      (weight, index) =>
        weight * (1 - submerged) + (CREATIVE_WORLD.regions[index]?.theme === 12 ? submerged : 0),
    ) as unknown as CreativeBiomeWeights;
    // Start at the arrival biome; ease later changes, including teleports, without frame-rate dependence.
    const alpha = 1 - Math.exp(-Math.max(0, deltaSeconds) / 0.6);
    const previous = this.weights;
    this.weights =
      previous === undefined
        ? target
        : (target.map(
            (weight, index) => (previous[index] ?? 0) + (weight - (previous[index] ?? 0)) * alpha,
          ) as unknown as CreativeBiomeWeights);
    return blendCreativeEnvironment(this.weights);
  }
}
